import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import { apiClient } from '../api/apiConfig';
import { BanknotesIcon, PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import { FinancialAccount } from '../types/finance';
import { Alert, Spinner, Badge, Button } from './ui';
import { Modal } from '../design/primitives';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';

const ACCOUNT_TYPE_OPTIONS = [
  { value: 'checking', label: 'Checking Account' },
  { value: 'savings', label: 'Savings Account' },
  { value: 'investment', label: 'Investment Account' },
  { value: 'brokerage', label: 'Brokerage Account' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'loan', label: 'Loan' },
  { value: 'mortgage', label: 'Mortgage' },
  { value: 'auto_loan', label: 'Auto Loan' },
  { value: 'retirement', label: 'Retirement Account' },
  { value: 'crypto', label: 'Cryptocurrency' },
  { value: 'cash', label: 'Cash' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'other', label: 'Other' },
];

interface VehicleFormData {
  vin: string;
  make: string;
  model: string;
  year: string;
  mileage: string;
  condition: string;
}

interface LoanFormData {
  original_amount: string;
  annual_interest_rate: string;
  monthly_payment: string;
  loan_term_months: string;
  loan_start_date: string;
  extra_payments_total: string;
}

interface AccountFormData {
  name: string;
  account_type: string;
  institution_name: string;
  account_number_last4: string;
  current_balance: string;
  currency: string;
  is_asset: boolean;
  notes: string;
  address: string;
  vehicle_metadata: VehicleFormData;
  loan_metadata: LoanFormData;
  linked_asset: string;
}

const emptyVehicleForm: VehicleFormData = {
  vin: '',
  make: '',
  model: '',
  year: '',
  mileage: '',
  condition: 'good',
};

const emptyLoanForm: LoanFormData = {
  original_amount: '',
  annual_interest_rate: '',
  monthly_payment: '',
  loan_term_months: '',
  loan_start_date: '',
  extra_payments_total: '0',
};

const LOAN_ACCOUNT_TYPES = ['loan', 'mortgage', 'auto_loan'];

const emptyForm: AccountFormData = {
  name: '',
  account_type: 'checking',
  institution_name: '',
  account_number_last4: '',
  current_balance: '0',
  currency: 'USD',
  is_asset: true,
  notes: '',
  address: '',
  vehicle_metadata: { ...emptyVehicleForm },
  loan_metadata: { ...emptyLoanForm },
  linked_asset: '',
};

export interface AccountsListRef {
  refresh: () => void;
}

const AccountsList = forwardRef<AccountsListRef>((props, ref) => {
  const [accounts, setAccounts] = useState<FinancialAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<FinancialAccount | null>(null);
  const [formData, setFormData] = useState<AccountFormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const openAddModal = () => {
    setFormData(emptyForm);
    setEditingAccount(null);
    setFormError(null);
    setShowAddModal(true);
  };

  const openEditModal = (account: FinancialAccount) => {
    const vm = account.vehicle_metadata;
    const lm = account.loan_metadata;
    setFormData({
      name: account.name,
      account_type: account.account_type,
      institution_name: account.institution_name || '',
      account_number_last4: account.account_number_last4 || '',
      current_balance: String(account.current_balance),
      currency: account.currency || 'USD',
      is_asset: account.is_asset,
      notes: account.notes || '',
      address: account.address || '',
      vehicle_metadata: vm ? {
        vin: vm.vin || '',
        make: vm.make || '',
        model: vm.model || '',
        year: vm.year != null ? String(vm.year) : '',
        mileage: vm.mileage != null ? String(vm.mileage) : '',
        condition: vm.condition || 'good',
      } : { ...emptyVehicleForm },
      loan_metadata: lm ? {
        original_amount: String(lm.original_amount),
        annual_interest_rate: String(lm.annual_interest_rate),
        monthly_payment: String(lm.monthly_payment),
        loan_term_months: String(lm.loan_term_months),
        loan_start_date: lm.loan_start_date || '',
        extra_payments_total: String(lm.extra_payments_total || 0),
      } : { ...emptyLoanForm },
      linked_asset: account.linked_asset ? String(account.linked_asset) : '',
    });
    setEditingAccount(account);
    setFormError(null);
    setShowAddModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setFormError('Account name is required');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const { vehicle_metadata: vmForm, loan_metadata: lmForm, linked_asset, ...rest } = formData;
      const payload: any = {
        ...rest,
        current_balance: parseFloat(formData.current_balance) || 0,
        linked_asset: linked_asset ? parseInt(linked_asset, 10) : null,
      };
      if (formData.account_type === 'vehicle') {
        payload.vehicle_metadata = {
          vin: vmForm.vin,
          make: vmForm.make,
          model: vmForm.model,
          year: vmForm.year ? parseInt(vmForm.year, 10) : null,
          mileage: vmForm.mileage ? parseInt(vmForm.mileage, 10) : null,
          condition: vmForm.condition,
        };
      }
      if (LOAN_ACCOUNT_TYPES.includes(formData.account_type) && lmForm.original_amount) {
        payload.loan_metadata = {
          original_amount: parseFloat(lmForm.original_amount),
          annual_interest_rate: parseFloat(lmForm.annual_interest_rate),
          monthly_payment: parseFloat(lmForm.monthly_payment),
          loan_term_months: parseInt(lmForm.loan_term_months, 10),
          loan_start_date: lmForm.loan_start_date,
          extra_payments_total: parseFloat(lmForm.extra_payments_total) || 0,
        };
      }
      if (editingAccount) {
        await apiClient.put(`/api/financial-accounts/${editingAccount.id}/`, payload);
      } else {
        await apiClient.post('/api/financial-accounts/', payload);
      }
      setShowAddModal(false);
      setRefreshKey(prev => prev + 1);
    } catch (err: any) {
      setFormError(err.response?.data?.detail || err.message || 'Failed to save account');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (account: FinancialAccount) => {
    if (!window.confirm(`Delete "${account.name}"? This will also delete all its transactions.`)) return;
    try {
      await apiClient.delete(`/api/financial-accounts/${account.id}/`);
      setRefreshKey(prev => prev + 1);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete account');
    }
  };

  const fetchAccounts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/api/financial-accounts/');
      setAccounts(response.data || []);
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError('Failed to load financial accounts.');
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    refresh: () => {
      setRefreshKey(prev => prev + 1);
    }
  }));

  useEffect(() => {
    fetchAccounts();
  }, [refreshKey]);

  const filteredAccounts = useMemo(() => {
    let filtered = accounts;

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(account =>
        account.name.toLowerCase().includes(query) ||
        account.institution_name?.toLowerCase().includes(query) ||
        account.account_type_display.toLowerCase().includes(query)
      );
    }

    // Filter by type
    if (filterType !== 'all') {
      if (filterType === 'assets') {
        filtered = filtered.filter(a => a.is_asset);
      } else if (filterType === 'liabilities') {
        filtered = filtered.filter(a => !a.is_asset);
      }
    }

    return filtered;
  }, [accounts, searchQuery, filterType]);

  const formatCurrency = (amount: number | string) => {
    // Ensure amount is a number (API might return strings)
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(isNaN(numericAmount) ? 0 : numericAmount);
  };

  const totalBalance = useMemo(() => {
    return filteredAccounts.reduce((sum, account) => {
      // Ensure balance is a number (API might return strings)
      const balance = Number(account.current_balance) || 0;
      return sum + (account.is_asset ? balance : -balance);
    }, 0);
  }, [filteredAccounts]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: SPACING[24] }}>
        <Spinner size="md" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
      {error && <Alert variant="error">{error}</Alert>}

      {/* Filters */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[12] }}>
        <div style={{ flex: 1 }}>
          <input
            type="text"
            placeholder="Search accounts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              display: 'block',
              width: '100%',
              paddingLeft: SPACING[16],
              paddingRight: SPACING[16],
              paddingTop: SPACING[8],
              paddingBottom: SPACING[8],
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              fontSize: TYPOGRAPHY.body.sizes.default,
              fontFamily: TYPOGRAPHY.body.fontFamily,
              color: COLORS.text.primary,
              backgroundColor: COLORS.bg.panel,
              outline: 'none',
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: SPACING[8] }}>
          <button
            onClick={() => setFilterType('all')}
            style={{
              paddingLeft: SPACING[16],
              paddingRight: SPACING[16],
              paddingTop: SPACING[8],
              paddingBottom: SPACING[8],
              fontSize: TYPOGRAPHY.size.sm,
              fontWeight: TYPOGRAPHY.weight.medium,
              borderRadius: RADIUS,
              cursor: 'pointer',
              border: filterType === 'all' ? 'none' : `1px solid ${COLORS.border.default}`,
              backgroundColor: filterType === 'all' ? COLORS.accent.default : COLORS.bg.panel,
              color: filterType === 'all' ? '#FFFFFF' : COLORS.text.primary,
            }}
          >
            All
          </button>
          <button
            onClick={() => setFilterType('assets')}
            style={{
              paddingLeft: SPACING[16],
              paddingRight: SPACING[16],
              paddingTop: SPACING[8],
              paddingBottom: SPACING[8],
              fontSize: TYPOGRAPHY.size.sm,
              fontWeight: TYPOGRAPHY.weight.medium,
              borderRadius: RADIUS,
              cursor: 'pointer',
              border: filterType === 'assets' ? 'none' : `1px solid ${COLORS.border.default}`,
              backgroundColor: filterType === 'assets' ? COLORS.status.success : COLORS.bg.panel,
              color: filterType === 'assets' ? '#FFFFFF' : COLORS.text.primary,
            }}
          >
            Assets
          </button>
          <button
            onClick={() => setFilterType('liabilities')}
            style={{
              paddingLeft: SPACING[16],
              paddingRight: SPACING[16],
              paddingTop: SPACING[8],
              paddingBottom: SPACING[8],
              fontSize: TYPOGRAPHY.size.sm,
              fontWeight: TYPOGRAPHY.weight.medium,
              borderRadius: RADIUS,
              cursor: 'pointer',
              border: filterType === 'liabilities' ? 'none' : `1px solid ${COLORS.border.default}`,
              backgroundColor: filterType === 'liabilities' ? COLORS.status.error : COLORS.bg.panel,
              color: filterType === 'liabilities' ? '#FFFFFF' : COLORS.text.primary,
            }}
          >
            Liabilities
          </button>
        </div>
      </div>

      {/* Header with total */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{
          fontSize: TYPOGRAPHY.headings.sizes.h2,
          fontWeight: TYPOGRAPHY.weight.medium,
          fontFamily: TYPOGRAPHY.headings.fontFamily,
          color: COLORS.text.primary,
          display: 'flex',
          alignItems: 'center',
          margin: 0,
        }}>
          <BanknotesIcon style={{ width: '20px', height: '20px', marginRight: SPACING[8], color: COLORS.status.success }} />
          Financial Accounts
          <span style={{
            marginLeft: SPACING[8],
            display: 'inline-flex',
            alignItems: 'center',
            paddingLeft: SPACING[8],
            paddingRight: SPACING[8],
            paddingTop: SPACING[4],
            paddingBottom: SPACING[4],
            borderRadius: '9999px',
            fontSize: TYPOGRAPHY.size.xs,
            fontWeight: TYPOGRAPHY.weight.medium,
            backgroundColor: COLORS.bg.hover,
            color: COLORS.text.primary,
          }}>
            {filteredAccounts.length}
          </span>
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[16] }}>
          <div style={{ fontSize: TYPOGRAPHY.size.sm }}>
            <span style={{ color: COLORS.text.secondary }}>Total Balance: </span>
            <span style={{
              fontWeight: TYPOGRAPHY.weight.bold,
              color: totalBalance >= 0 ? COLORS.status.success : COLORS.status.error,
            }}>
              {formatCurrency(totalBalance)}
            </span>
          </div>
          <Button variant="primary" size="sm" leftIcon={<PlusIcon style={{ height: '14px', width: '14px' }} />} onClick={openAddModal}>
            Add Account
          </Button>
        </div>
      </div>

      {filteredAccounts.length === 0 ? (
        <div style={{
          textAlign: 'center',
          paddingTop: SPACING[48],
          paddingBottom: SPACING[48],
          backgroundColor: COLORS.bg.base,
          borderRadius: RADIUS,
        }}>
          <BanknotesIcon style={{ margin: '0 auto', height: '48px', width: '48px', color: COLORS.text.tertiary }} />
          <p style={{
            marginTop: SPACING[8],
            fontSize: TYPOGRAPHY.size.sm,
            fontWeight: TYPOGRAPHY.weight.medium,
            color: COLORS.text.primary,
          }}>No accounts found</p>
          <p style={{
            marginTop: SPACING[4],
            fontSize: TYPOGRAPHY.size.sm,
            color: COLORS.text.secondary,
          }}>Connect your bank or add accounts manually</p>
          <Button variant="primary" style={{ marginTop: SPACING[16] }} leftIcon={<PlusIcon style={{ height: '16px', width: '16px' }} />} onClick={openAddModal}>
            Add Account
          </Button>
        </div>
      ) : (
        <div style={{
          overflowX: 'auto',
          border: `1px solid ${COLORS.border.default}`,
          borderRadius: RADIUS,
        }}>
          <table style={{
            minWidth: '100%',
            backgroundColor: COLORS.bg.panel,
            borderCollapse: 'collapse',
          }}>
            <thead style={{ backgroundColor: COLORS.bg.base }}>
              <tr>
                <th style={{
                  padding: `${SPACING[12]} ${SPACING[16]}`,
                  textAlign: 'left',
                  fontSize: TYPOGRAPHY.size.xs,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>Account</th>
                <th style={{
                  padding: `${SPACING[12]} ${SPACING[16]}`,
                  textAlign: 'left',
                  fontSize: TYPOGRAPHY.size.xs,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>Institution</th>
                <th style={{
                  padding: `${SPACING[12]} ${SPACING[16]}`,
                  textAlign: 'left',
                  fontSize: TYPOGRAPHY.size.xs,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>Type</th>
                <th style={{
                  padding: `${SPACING[12]} ${SPACING[16]}`,
                  textAlign: 'left',
                  fontSize: TYPOGRAPHY.size.xs,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>Balance</th>
                <th style={{
                  padding: `${SPACING[12]} ${SPACING[16]}`,
                  textAlign: 'left',
                  fontSize: TYPOGRAPHY.size.xs,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>Status</th>
                <th style={{
                  padding: `${SPACING[12]} ${SPACING[16]}`,
                  textAlign: 'right',
                  fontSize: TYPOGRAPHY.size.xs,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>Actions</th>
              </tr>
            </thead>
            <tbody style={{ backgroundColor: COLORS.bg.panel }}>
              {filteredAccounts.map((account, index) => (
                <tr
                  key={account.id}
                  style={{
                    borderTop: index > 0 ? `1px solid ${COLORS.border.default}` : 'none',
                  }}
                >
                  <td style={{ padding: `${SPACING[12]} ${SPACING[16]}` }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{
                        fontSize: TYPOGRAPHY.size.sm,
                        fontWeight: TYPOGRAPHY.weight.medium,
                        color: COLORS.text.primary,
                      }}>{account.name}</span>
                      {account.account_number_last4 && (
                        <span style={{
                          fontSize: TYPOGRAPHY.size.xs,
                          color: COLORS.text.secondary,
                        }}>...{account.account_number_last4}</span>
                      )}
                    </div>
                  </td>
                  <td style={{
                    padding: `${SPACING[12]} ${SPACING[16]}`,
                    fontSize: TYPOGRAPHY.size.sm,
                    color: COLORS.text.primary,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[4] }}>
                      {account.institution_name || '-'}
                      {account.plaid_account_id && (
                        <span style={{
                          fontSize: '10px',
                          fontWeight: TYPOGRAPHY.weight.medium,
                          color: COLORS.accent.default,
                          backgroundColor: `${COLORS.accent.default}15`,
                          padding: `1px ${SPACING[4]}`,
                          borderRadius: RADIUS,
                        }}>Plaid</span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: `${SPACING[12]} ${SPACING[16]}`, fontSize: TYPOGRAPHY.size.sm }}>
                    <Badge
                      variant={account.is_asset ? 'success' : 'danger'}
                      size="sm"
                    >
                      {account.account_type_display}
                    </Badge>
                  </td>
                  <td style={{ padding: `${SPACING[12]} ${SPACING[16]}`, whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{
                        fontSize: TYPOGRAPHY.size.sm,
                        fontWeight: TYPOGRAPHY.weight.bold,
                        color: account.is_asset ? COLORS.status.success : COLORS.status.error,
                      }}>
                        {formatCurrency(account.current_balance)}
                      </span>
                      {account.available_balance != null && account.available_balance !== 0 && account.available_balance !== account.current_balance && (
                        <span style={{
                          fontSize: TYPOGRAPHY.size.xs,
                          color: COLORS.text.secondary,
                        }}>
                          Available: {formatCurrency(account.available_balance)}
                        </span>
                      )}
                      {account.equity != null && (
                        <span style={{
                          fontSize: TYPOGRAPHY.size.xs,
                          color: account.equity >= 0 ? COLORS.status.success : COLORS.status.error,
                        }}>
                          Equity: {formatCurrency(account.equity)} ({account.linked_asset_name})
                        </span>
                      )}
                      {account.linked_loan && (
                        <span style={{
                          fontSize: TYPOGRAPHY.size.xs,
                          color: account.linked_loan.equity >= 0 ? COLORS.status.success : COLORS.status.error,
                        }}>
                          Loan: {formatCurrency(account.linked_loan.balance)} | Equity: {formatCurrency(account.linked_loan.equity)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{
                    padding: `${SPACING[12]} ${SPACING[16]}`,
                    whiteSpace: 'nowrap',
                    fontSize: TYPOGRAPHY.size.sm,
                  }}>
                    <Badge
                      variant={account.is_active ? 'success' : 'default'}
                      size="sm"
                    >
                      {account.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td style={{ padding: `${SPACING[12]} ${SPACING[16]}`, textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: SPACING[8], justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => openEditModal(account)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer', padding: SPACING[4],
                          color: COLORS.text.secondary, borderRadius: RADIUS,
                        }}
                        title="Edit"
                      >
                        <PencilIcon style={{ width: '16px', height: '16px' }} />
                      </button>
                      <button
                        onClick={() => handleDelete(account)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer', padding: SPACING[4],
                          color: COLORS.status.error, borderRadius: RADIUS,
                        }}
                        title="Delete"
                      >
                        <TrashIcon style={{ width: '16px', height: '16px' }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Add/Edit Account Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={editingAccount ? 'Edit Account' : 'Add Account'}
        footer={
          <div style={{ display: 'flex', gap: SPACING[8], justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : (editingAccount ? 'Update' : 'Add Account')}
            </Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
          {formError && <Alert variant="error">{formError}</Alert>}

          <div>
            <label style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary, marginBottom: SPACING[4] }}>
              Account Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g. Mercury Business Checking"
              style={{
                width: '100%', padding: SPACING[8], border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS, fontSize: TYPOGRAPHY.size.sm, fontFamily: TYPOGRAPHY.body.fontFamily,
                color: COLORS.text.primary, backgroundColor: COLORS.bg.base, outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING[12] }}>
            <div>
              <label style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary, marginBottom: SPACING[4] }}>
                Account Type
              </label>
              <select
                value={formData.account_type}
                onChange={e => {
                  const type = e.target.value;
                  const isLiability = ['credit_card', 'loan', 'mortgage', 'auto_loan'].includes(type);
                  setFormData(prev => ({ ...prev, account_type: type, is_asset: !isLiability }));
                }}
                style={{
                  width: '100%', padding: SPACING[8], border: `1px solid ${COLORS.border.default}`,
                  borderRadius: RADIUS, fontSize: TYPOGRAPHY.size.sm, fontFamily: TYPOGRAPHY.body.fontFamily,
                  color: COLORS.text.primary, backgroundColor: COLORS.bg.base, outline: 'none',
                }}
              >
                {ACCOUNT_TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary, marginBottom: SPACING[4] }}>
                Institution
              </label>
              <input
                type="text"
                value={formData.institution_name}
                onChange={e => setFormData(prev => ({ ...prev, institution_name: e.target.value }))}
                placeholder="e.g. Mercury, Chase"
                style={{
                  width: '100%', padding: SPACING[8], border: `1px solid ${COLORS.border.default}`,
                  borderRadius: RADIUS, fontSize: TYPOGRAPHY.size.sm, fontFamily: TYPOGRAPHY.body.fontFamily,
                  color: COLORS.text.primary, backgroundColor: COLORS.bg.base, outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          {formData.account_type === 'real_estate' && (
            <div>
              <label style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary, marginBottom: SPACING[4] }}>
                Property Address (for Zillow valuation)
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={e => setFormData(prev => ({ ...prev, address: e.target.value }))}
                placeholder="e.g. 183 Scotland Yard Blvd, Saint Johns, FL 32259"
                style={{
                  width: '100%', padding: SPACING[8], border: `1px solid ${COLORS.border.default}`,
                  borderRadius: RADIUS, fontSize: TYPOGRAPHY.size.sm, fontFamily: TYPOGRAPHY.body.fontFamily,
                  color: COLORS.text.primary, backgroundColor: COLORS.bg.base, outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          )}

          {formData.account_type === 'vehicle' && (
            <>
              <div style={{
                padding: SPACING[12],
                backgroundColor: COLORS.bg.base,
                borderRadius: RADIUS,
                border: `1px solid ${COLORS.border.default}`,
                display: 'flex', flexDirection: 'column', gap: SPACING[12],
              }}>
                <div style={{ fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.secondary }}>
                  Vehicle Details
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary, marginBottom: SPACING[4] }}>
                    VIN
                  </label>
                  <input
                    type="text"
                    maxLength={17}
                    value={formData.vehicle_metadata.vin}
                    onChange={e => setFormData(prev => ({ ...prev, vehicle_metadata: { ...prev.vehicle_metadata, vin: e.target.value.toUpperCase() } }))}
                    placeholder="e.g. 1HGBH41JXMN109186"
                    style={{
                      width: '100%', padding: SPACING[8], border: `1px solid ${COLORS.border.default}`,
                      borderRadius: RADIUS, fontSize: TYPOGRAPHY.size.sm, fontFamily: TYPOGRAPHY.body.fontFamily,
                      color: COLORS.text.primary, backgroundColor: COLORS.bg.panel, outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: SPACING[12] }}>
                  <div>
                    <label style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary, marginBottom: SPACING[4] }}>
                      Make
                    </label>
                    <input
                      type="text"
                      value={formData.vehicle_metadata.make}
                      onChange={e => setFormData(prev => ({ ...prev, vehicle_metadata: { ...prev.vehicle_metadata, make: e.target.value } }))}
                      placeholder="e.g. Toyota"
                      style={{
                        width: '100%', padding: SPACING[8], border: `1px solid ${COLORS.border.default}`,
                        borderRadius: RADIUS, fontSize: TYPOGRAPHY.size.sm, fontFamily: TYPOGRAPHY.body.fontFamily,
                        color: COLORS.text.primary, backgroundColor: COLORS.bg.panel, outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary, marginBottom: SPACING[4] }}>
                      Model
                    </label>
                    <input
                      type="text"
                      value={formData.vehicle_metadata.model}
                      onChange={e => setFormData(prev => ({ ...prev, vehicle_metadata: { ...prev.vehicle_metadata, model: e.target.value } }))}
                      placeholder="e.g. Camry"
                      style={{
                        width: '100%', padding: SPACING[8], border: `1px solid ${COLORS.border.default}`,
                        borderRadius: RADIUS, fontSize: TYPOGRAPHY.size.sm, fontFamily: TYPOGRAPHY.body.fontFamily,
                        color: COLORS.text.primary, backgroundColor: COLORS.bg.panel, outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary, marginBottom: SPACING[4] }}>
                      Year
                    </label>
                    <input
                      type="number"
                      value={formData.vehicle_metadata.year}
                      onChange={e => setFormData(prev => ({ ...prev, vehicle_metadata: { ...prev.vehicle_metadata, year: e.target.value } }))}
                      placeholder="e.g. 2022"
                      style={{
                        width: '100%', padding: SPACING[8], border: `1px solid ${COLORS.border.default}`,
                        borderRadius: RADIUS, fontSize: TYPOGRAPHY.size.sm, fontFamily: TYPOGRAPHY.body.fontFamily,
                        color: COLORS.text.primary, backgroundColor: COLORS.bg.panel, outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING[12] }}>
                  <div>
                    <label style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary, marginBottom: SPACING[4] }}>
                      Mileage
                    </label>
                    <input
                      type="number"
                      value={formData.vehicle_metadata.mileage}
                      onChange={e => setFormData(prev => ({ ...prev, vehicle_metadata: { ...prev.vehicle_metadata, mileage: e.target.value } }))}
                      placeholder="e.g. 25000"
                      style={{
                        width: '100%', padding: SPACING[8], border: `1px solid ${COLORS.border.default}`,
                        borderRadius: RADIUS, fontSize: TYPOGRAPHY.size.sm, fontFamily: TYPOGRAPHY.body.fontFamily,
                        color: COLORS.text.primary, backgroundColor: COLORS.bg.panel, outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary, marginBottom: SPACING[4] }}>
                      Condition
                    </label>
                    <select
                      value={formData.vehicle_metadata.condition}
                      onChange={e => setFormData(prev => ({ ...prev, vehicle_metadata: { ...prev.vehicle_metadata, condition: e.target.value } }))}
                      style={{
                        width: '100%', padding: SPACING[8], border: `1px solid ${COLORS.border.default}`,
                        borderRadius: RADIUS, fontSize: TYPOGRAPHY.size.sm, fontFamily: TYPOGRAPHY.body.fontFamily,
                        color: COLORS.text.primary, backgroundColor: COLORS.bg.panel, outline: 'none',
                      }}
                    >
                      <option value="excellent">Excellent</option>
                      <option value="good">Good</option>
                      <option value="fair">Fair</option>
                      <option value="poor">Poor</option>
                    </select>
                  </div>
                </div>
              </div>
            </>
          )}

          {LOAN_ACCOUNT_TYPES.includes(formData.account_type) && (
            <>
              <div style={{
                padding: SPACING[12],
                backgroundColor: COLORS.bg.base,
                borderRadius: RADIUS,
                border: `1px solid ${COLORS.border.default}`,
                display: 'flex', flexDirection: 'column', gap: SPACING[12],
              }}>
                <div style={{ fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.secondary }}>
                  Loan Amortization Details
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING[12] }}>
                  <div>
                    <label style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary, marginBottom: SPACING[4] }}>
                      Original Loan Amount ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.loan_metadata.original_amount}
                      onChange={e => setFormData(prev => ({ ...prev, loan_metadata: { ...prev.loan_metadata, original_amount: e.target.value } }))}
                      placeholder="e.g. 25000"
                      style={{
                        width: '100%', padding: SPACING[8], border: `1px solid ${COLORS.border.default}`,
                        borderRadius: RADIUS, fontSize: TYPOGRAPHY.size.sm, fontFamily: TYPOGRAPHY.body.fontFamily,
                        color: COLORS.text.primary, backgroundColor: COLORS.bg.panel, outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary, marginBottom: SPACING[4] }}>
                      Annual Interest Rate (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.loan_metadata.annual_interest_rate}
                      onChange={e => setFormData(prev => ({ ...prev, loan_metadata: { ...prev.loan_metadata, annual_interest_rate: e.target.value } }))}
                      placeholder="e.g. 5.99"
                      style={{
                        width: '100%', padding: SPACING[8], border: `1px solid ${COLORS.border.default}`,
                        borderRadius: RADIUS, fontSize: TYPOGRAPHY.size.sm, fontFamily: TYPOGRAPHY.body.fontFamily,
                        color: COLORS.text.primary, backgroundColor: COLORS.bg.panel, outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING[12] }}>
                  <div>
                    <label style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary, marginBottom: SPACING[4] }}>
                      Monthly Payment ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.loan_metadata.monthly_payment}
                      onChange={e => setFormData(prev => ({ ...prev, loan_metadata: { ...prev.loan_metadata, monthly_payment: e.target.value } }))}
                      placeholder="e.g. 485.00"
                      style={{
                        width: '100%', padding: SPACING[8], border: `1px solid ${COLORS.border.default}`,
                        borderRadius: RADIUS, fontSize: TYPOGRAPHY.size.sm, fontFamily: TYPOGRAPHY.body.fontFamily,
                        color: COLORS.text.primary, backgroundColor: COLORS.bg.panel, outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary, marginBottom: SPACING[4] }}>
                      Loan Term (months)
                    </label>
                    <input
                      type="number"
                      value={formData.loan_metadata.loan_term_months}
                      onChange={e => setFormData(prev => ({ ...prev, loan_metadata: { ...prev.loan_metadata, loan_term_months: e.target.value } }))}
                      placeholder="e.g. 60"
                      style={{
                        width: '100%', padding: SPACING[8], border: `1px solid ${COLORS.border.default}`,
                        borderRadius: RADIUS, fontSize: TYPOGRAPHY.size.sm, fontFamily: TYPOGRAPHY.body.fontFamily,
                        color: COLORS.text.primary, backgroundColor: COLORS.bg.panel, outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING[12] }}>
                  <div>
                    <label style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary, marginBottom: SPACING[4] }}>
                      Loan Start Date
                    </label>
                    <input
                      type="date"
                      value={formData.loan_metadata.loan_start_date}
                      onChange={e => setFormData(prev => ({ ...prev, loan_metadata: { ...prev.loan_metadata, loan_start_date: e.target.value } }))}
                      style={{
                        width: '100%', padding: SPACING[8], border: `1px solid ${COLORS.border.default}`,
                        borderRadius: RADIUS, fontSize: TYPOGRAPHY.size.sm, fontFamily: TYPOGRAPHY.body.fontFamily,
                        color: COLORS.text.primary, backgroundColor: COLORS.bg.panel, outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary, marginBottom: SPACING[4] }}>
                      Extra Payments Total ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.loan_metadata.extra_payments_total}
                      onChange={e => setFormData(prev => ({ ...prev, loan_metadata: { ...prev.loan_metadata, extra_payments_total: e.target.value } }))}
                      placeholder="0"
                      style={{
                        width: '100%', padding: SPACING[8], border: `1px solid ${COLORS.border.default}`,
                        borderRadius: RADIUS, fontSize: TYPOGRAPHY.size.sm, fontFamily: TYPOGRAPHY.body.fontFamily,
                        color: COLORS.text.primary, backgroundColor: COLORS.bg.panel, outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {!formData.is_asset && (
            <div>
              <label style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary, marginBottom: SPACING[4] }}>
                Link to Asset (for equity calculation)
              </label>
              <select
                value={formData.linked_asset}
                onChange={e => setFormData(prev => ({ ...prev, linked_asset: e.target.value }))}
                style={{
                  width: '100%', padding: SPACING[8], border: `1px solid ${COLORS.border.default}`,
                  borderRadius: RADIUS, fontSize: TYPOGRAPHY.size.sm, fontFamily: TYPOGRAPHY.body.fontFamily,
                  color: COLORS.text.primary, backgroundColor: COLORS.bg.base, outline: 'none',
                }}
              >
                <option value="">None</option>
                {accounts.filter(a => a.is_asset && a.is_active && a.id !== editingAccount?.id).map(a => (
                  <option key={a.id} value={String(a.id)}>{a.name} ({formatCurrency(a.current_balance)})</option>
                ))}
              </select>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING[12] }}>
            <div>
              <label style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary, marginBottom: SPACING[4] }}>
                Current Balance
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.current_balance}
                onChange={e => setFormData(prev => ({ ...prev, current_balance: e.target.value }))}
                style={{
                  width: '100%', padding: SPACING[8], border: `1px solid ${COLORS.border.default}`,
                  borderRadius: RADIUS, fontSize: TYPOGRAPHY.size.sm, fontFamily: TYPOGRAPHY.body.fontFamily,
                  color: COLORS.text.primary, backgroundColor: COLORS.bg.base, outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary, marginBottom: SPACING[4] }}>
                Last 4 Digits
              </label>
              <input
                type="text"
                maxLength={4}
                value={formData.account_number_last4}
                onChange={e => setFormData(prev => ({ ...prev, account_number_last4: e.target.value.replace(/\D/g, '') }))}
                placeholder="1234"
                style={{
                  width: '100%', padding: SPACING[8], border: `1px solid ${COLORS.border.default}`,
                  borderRadius: RADIUS, fontSize: TYPOGRAPHY.size.sm, fontFamily: TYPOGRAPHY.body.fontFamily,
                  color: COLORS.text.primary, backgroundColor: COLORS.bg.base, outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
            <input
              type="checkbox"
              checked={formData.is_asset}
              onChange={e => setFormData(prev => ({ ...prev, is_asset: e.target.checked }))}
              id="is_asset_checkbox"
            />
            <label htmlFor="is_asset_checkbox" style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.primary }}>
              This is an asset (uncheck for liabilities like credit cards, loans)
            </label>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary, marginBottom: SPACING[4] }}>
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={2}
              style={{
                width: '100%', padding: SPACING[8], border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS, fontSize: TYPOGRAPHY.size.sm, fontFamily: TYPOGRAPHY.body.fontFamily,
                color: COLORS.text.primary, backgroundColor: COLORS.bg.base, outline: 'none',
                resize: 'vertical', boxSizing: 'border-box',
              }}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
});

export default AccountsList;
